import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  ListGroup,
  Alert,
  Spinner,
} from "react-bootstrap";
import Stepper from "@/QuestionnaireBuilder/Stepper";
import { useQuestionnaire } from "@/QuestionnaireBuilder/QuestionnaireContext";

export default function QuestionnaireReview() {
  const PURPLE = "#2E2868";
  const navigate = useNavigate();
  const { id } = useParams();

  const [successMsg, setSuccessMsg] = useState(null);

  const { meta, questions, settings, saveQuestionnaire, loading, error } =
    useQuestionnaire();

  const goBack = () => {
    if (id) navigate(`/app/customers/questionnaires/${id}/questions`);
    else navigate(`/app/customers/questionnaires/questions`);
  };

  const publish = async () => {
    try {
      const result = await saveQuestionnaire(id ? parseInt(id) : undefined);
      if (result?.success) {
        setSuccessMsg("Questionnaire Published Successfully!");
        setTimeout(() => {
          navigate("/app/customers/questionnaires");
        }, 1500);
      }
    } catch (err) {
      console.error("Publish failed", err);
    }
  };

const actionText = (logic) => {
  if (!logic || !logic.action) return "Proceed";

  if (logic.action === "goto") {
    const targetQuestionIndex = questions.findIndex(
      (q) => q.id === logic.targetId
    );
    if (targetQuestionIndex !== -1)
      return `Go to Question ${targetQuestionIndex + 1}`;
    return "Go to question (Deleted)";
  }

  if (logic.action === "submit") {
    const ep = settings.endPages.find(
      (p) => String(p.id) === String(logic.endPageId)
    );
    const outcomeName = ep ? ep.name : "End Page";
    
    
    return `End -> ${outcomeName}`;
  }

  return "Proceed";
};

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          marginTop: "20px",
        }}
      >
        <Stepper
          labels={["Setup", "Questions", "Review"]}
          activeIndex={2}
          paths={[
            "/app/customers/questionnaires/setup",
            "/app/customers/questionnaires/questions",
            "/app/customers/questionnaires/review",
          ]}
        />
      </div>

      <Container fluid style={{ maxWidth: 1100, marginTop: 30 }}>
        <Row className="justify-content-center">
          <Col xs={12}>
            <Card
              style={{
                border: "3px solid #eaeaea",
                borderRadius: 12,
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                minHeight: 400,
              }}
            >
              <Card.Body style={{ padding: "2.5rem" }}>
                <div style={{ textAlign: "center", marginBottom: "1.25rem" }}>
                  <h5 style={{ fontWeight: 600, color: PURPLE, marginBottom: 6 }}>
                    Review Questionnaire
                  </h5>
                  <div style={{ color: "#6c6f82", fontSize: 14 }}>
                    Review all settings and questions before publishing.
                  </div>
                </div>

                <div
                  style={{
                    height: 1,
                    background: "#d7d7df",
                    width: 220,
                    margin: "0 auto 1.25rem",
                  }}
                />

                {successMsg && (
                  <Alert
                    variant="success"
                    style={{ borderRadius: 12, textAlign: "center" }}
                  >
                    {successMsg}
                  </Alert>
                )}
                {error && (
                  <Alert variant="danger" style={{ borderRadius: 12 }}>
                    {error}
                  </Alert>
                )}

                {!meta.name && questions.length === 0 ? (
                  <div style={{ textAlign: "center", color: "#6c6f82" }}>
                    No data available. Please go back.
                  </div>
                ) : (
                  <>
                    <div style={{ marginBottom: 16 }}>
                      <div
                        style={{ fontWeight: 700, fontSize: 18, color: PURPLE }}
                      >
                        {meta.name || "Untitled"}
                      </div>
                      <div style={{ color: "#6c757d", marginBottom: 8 }}>
                        {meta.purpose || "No purpose defined."}
                      </div>
                    </div>

                    <Card style={{ border: "1px solid #e7e7ef" }}>
                      <ListGroup variant="flush">
                        {questions.map((q, idx) => (
                          <ListGroup.Item key={q.id} style={{ padding: 16 }}>
                            {/* Updated Header Layout: alignItems center + flex for alignment */}
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 600,
                                  color: "#3f3b6a",
                                  flex: 1,
                                  marginRight: 15,
                                }}
                              >
                                {`Q${idx + 1} — ${q.label || "Untitled"}`}
                              </div>
                              <div style={{ display: "flex", whiteSpace: "nowrap" }}>
                                <Badge
                                  bg="light"
                                  text="dark"
                                  style={{ marginRight: 8 }}
                                >
                                  {q.type === "multiple"
                                    ? "Multiple choice"
                                    : "Short answer"}
                                </Badge>
                                <Badge bg={q.required ? "danger" : "secondary"}>
                                  {q.required ? "Required" : "Optional"}
                                </Badge>
                              </div>
                            </div>

                            {/* --- Multiple Choice Section --- */}
                            {q.type === "multiple" && (
                              <div style={{ marginTop: 10 }}>
                                {(q.options || []).map((opt, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr 1fr",
                                      gap: 8,
                                      padding: "8px 10px",
                                      borderRadius: 8,
                                      border: "1px solid #f0f0f6",
                                      marginBottom: 8,
                                      background: "#fcfcff",
                                    }}
                                  >
                                    <div>
                                      <div style={{ fontSize: 11, color: "#999" }}>
                                        Answer
                                      </div>
                                      <div
                                        style={{ fontWeight: 600, fontSize: 13 }}
                                      >
                                        {opt.text || `Option ${i + 1}`}
                                      </div>
                                    </div>
                                    <div>
                                      <div style={{ fontSize: 11, color: "#999" }}>
                                        Logic
                                      </div>
                                      <div
                                        style={{
                                          fontWeight: 600,
                                          fontSize: 13,
                                          color:
                                            opt.logic?.action === "submit"
                                              ? "#28a745"
                                              : "#555",
                                        }}
                                      >
                                        {actionText(opt.logic)}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Short Answer Section (Visual Update) */}
                            {q.type === "short" && q.logic && (
                              <div style={{ marginTop: 10 }}>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: 8,
                                    padding: "8px 10px",
                                    borderRadius: 8,
                                    border: "1px solid #f0f0f6",
                                    background: "#fcfcff",
                                  }}
                                >
                                  {/* Answer Column */}
                                  <div>
                                    <div style={{ fontSize: 11, color: "#999" }}>
                                      Answer
                                    </div>
                                    <div style={{ fontWeight: 600, fontSize: 13, color: "#666", fontStyle: "italic" }}>
                                      Any text input
                                    </div>
                                  </div>

                                  {/* Logic Column */}
                                  <div>
                                    <div style={{ fontSize: 11, color: "#999" }}>
                                      Logic
                                    </div>
                                    <div
                                      style={{
                                        fontWeight: 600,
                                        fontSize: 13,
                                        color:
                                          q.logic.action === "submit"
                                            ? "#28a745"
                                            : "#555",
                                      }}
                                    >
                                      {actionText(q.logic)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </Card>
                  </>
                )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginTop: "2rem",
                  }}
                >
                  <Button
                    variant="outline-secondary"
                    style={{
                      padding: "0.6rem 2rem",
                      borderRadius: 25,
                      borderColor: PURPLE,
                      color: PURPLE,
                    }}
                    onClick={goBack}
                    disabled={loading}
                  >
                    Back
                  </Button>
                  <Button
                    style={{
                      padding: "0.6rem 2.5rem",
                      borderRadius: 25,
                      backgroundColor: PURPLE,
                      borderColor: "#6c63ff",
                      fontWeight: 500,
                      minWidth: 140,
                    }}
                    onClick={publish}
                    disabled={
                      !meta.name ||
                      questions.length === 0 ||
                      loading ||
                      successMsg
                    }
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          style={{ marginRight: 8 }}
                        />{" "}
                        Saving...
                      </>
                    ) : (
                      "Publish"
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}